/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, ChevronDown, EyeOff, Flag, MessageCircle, Pencil, Send, Share2, VolumeX } from "lucide-react";
import { toast } from "sonner";

import FullScreenImage from "@/components/FullScreenImage";
import IosInstallPrompt from "@/components/IosInstallPrompt";
import KindredShareSheet from "@/components/KindredShareSheet";
import KindredSharedContent from "@/components/KindredSharedContent";
import KinfeedMedia from "@/components/KinfeedMedia";
import MentionComposerField from "@/components/MentionComposerField";
import MessageActions from "@/components/MessageActions";
import PushNotificationPrompt from "@/components/PushNotificationPrompt";
import ReactionBar from "@/components/ReactionBar";
import ReportSheet from "@/components/ReportSheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { fetchFeedPreferences, hideFeedPost, muteFeedMember, unhideFeedPost, unmuteFeedMember } from "@/lib/feedPreferences";
import { createKinfeedComment, fetchKinfeedPage, toggleKinfeedPostReaction } from "@/lib/kinfeedApi";
import { buildKindredWebUrl, composeKindredContent, getKindredSharePreview, parseKindredShareContent, type KindredSharePayload } from "@/lib/kindredShare";
import { formatPublicLocation } from "@/lib/locationPrivacy";
import { dispatchMentionNotifications, searchAllMentionSuggestions } from "@/lib/mentions";
import { dispatchNotification } from "@/lib/pushNotifications";
import { fetchSavedFeedPostIds, toggleSavedFeedPost } from "@/lib/savedFeedPosts";
import { resolveSignedStorageUrls, resolveStoredObjectUrl } from "@/lib/storageObjectUrls";
import {
  combineKinfeedPages,
  filterItemsByKinfeedTopic,
  filterKinfeedPostsForViewer,
  getFeedReplyPreviewText,
  getKinfeedTopicTag,
  groupFeedPostReactions,
  KINFEED_PAGE_SIZE,
  KINFEED_POST_MAX_LENGTH,
  KINFEED_TOPIC_FILTER_ALL,
  KINFEED_TOPIC_TAGS,
  isKinfeedPostWithinLimit,
  normalizeKinfeedTopicTags,
  type FeedCommentPreview,
  type KinfeedFeedMode,
  type KinfeedTopicFilter,
  type MentionDraft,
} from "../../shared/community";
import {
  canEditOwnedContentOnly,
  canRemoveKinfeedOrChannelContent,
  isAdminModeratingOthersContent,
} from "../../shared/system";
import {
  AppBottomSheetSurface,
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppLoadingState,
  AppSegmentedControl,
  AppText,
  AppTextArea,
} from "../../ui-foundation";

const LAST_READ_SETTLE_MS = 650;

const mergeById = <T extends { id: string }>(current: T[], next: T[]) => combineKinfeedPages(current, next);

const getPostPreviewText = (content: string, maxLength = 150) => {
  const parsed = parseKindredShareContent(content);
  const text = (parsed.payload ? parsed.text : content).replace(/\s+/g, " ").trim();
  return text.length > maxLength ? `${text.slice(0, maxLength - 3).trimEnd()}...` : text;
};

const formatCommentCount = (count: number) => `${count} comment${count === 1 ? "" : "s"}`;

const fetchSavedRows = async (userId: string) => {
  const { data, error } = await supabase
    .from("saved_feed_posts" as any)
    .select(`
      created_at,
      feed_posts!inner(*, profiles(id, display_name, avatar_url, postcode, country_code))
    `)
    .eq("user_id", userId)
    .eq("feed_posts.is_deleted", false)
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data ?? [])
    .map((row: any) => row.feed_posts)
    .map((post: any) => (Array.isArray(post) ? post[0] : post))
    .filter(Boolean);
};

const fetchSavedEngagement = async (postIds: string[]) => {
  if (!postIds.length) return { likes: [], reactions: [], recentComments: [] as FeedCommentPreview[] };

  const [{ data: likes, error: likesError }, { data: reactions, error: reactionsError }, { data: comments, error: commentsError }] = await Promise.all([
    supabase.from("feed_post_likes" as any).select("id, post_id, user_id").in("post_id", postIds),
    supabase.from("feed_post_reactions").select("id, post_id, user_id, emoji").in("post_id", postIds),
    supabase
      .from("feed_comments" as any)
      .select("id, post_id, content, created_at, user_id, parent_id, profiles(display_name, avatar_url)")
      .eq("is_deleted", false)
      .in("post_id", postIds)
      .order("created_at", { ascending: false })
      .limit(postIds.length * 3),
  ]);

  if (likesError) throw likesError;
  if (reactionsError) throw reactionsError;
  if (commentsError) throw commentsError;
  return { likes: likes ?? [], reactions: reactions ?? [], recentComments: (comments ?? []) as FeedCommentPreview[] };
};

const CommunityFeed = () => {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const [feedMode, setFeedMode] = useState<KinfeedFeedMode>("local");
  const [posts, setPosts] = useState<any[]>([]);
  const [allSavedPosts, setAllSavedPosts] = useState<any[]>([]);
  const [savedPostIds, setSavedPostIds] = useState<Set<string>>(new Set());
  const [likes, setLikes] = useState<any[]>([]);
  const [postReactions, setPostReactions] = useState<any[]>([]);
  const [recentComments, setRecentComments] = useState<FeedCommentPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [newPostCount, setNewPostCount] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [selectedTopic, setSelectedTopic] = useState<KinfeedTopicFilter>(KINFEED_TOPIC_FILTER_ALL);
  const [topicPickerOpen, setTopicPickerOpen] = useState(false);
  const [expandedCommentPostId, setExpandedCommentPostId] = useState<string | null>(null);
  const [inlineComment, setInlineComment] = useState("");
  const [inlineMentions, setInlineMentions] = useState<MentionDraft[]>([]);
  const [sendingComment, setSendingComment] = useState(false);
  const [reportPostId, setReportPostId] = useState<string | null>(null);
  const [fullScreenImages, setFullScreenImages] = useState<{ images: string[]; index: number } | null>(null);
  const [resolvedPhotoUrls, setResolvedPhotoUrls] = useState<Map<string, string>>(new Map());
  const [resolvedVideoUrls, setResolvedVideoUrls] = useState<Map<string, string>>(new Map());

  const postsRef = useRef<any[]>([]);
  const savedPostsRef = useRef<any[]>([]);
  const feedModeRef = useRef<KinfeedFeedMode>(feedMode);
  const preferencesRef = useRef({ hiddenPostIds: new Set<string>(), mutedMemberIds: new Set<string>() });
  const loadingMoreRef = useRef(false);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const postRefs = useRef<Record<string, HTMLElement | null>>({});
  const restoreTargetRef = useRef<string | null>(null);
  const restoreAttemptedRef = useRef(false);
  const saveTimeoutRef = useRef<number | null>(null);

  postsRef.current = posts;
  savedPostsRef.current = allSavedPosts;
  feedModeRef.current = feedMode;

  const storageKey = user?.id ? `kindred:kinfeed:last-read:${user.id}:${feedMode}` : null;

  const rememberLastRead = useCallback((postId: string) => {
    if (storageKey) localStorage.setItem(storageKey, postId);
  }, [storageKey]);

  const loadPage = useCallback(async (mode: KinfeedFeedMode, reset: boolean) => {
    if (!user?.id || loadingMoreRef.current) {
      if (!user?.id) setLoading(false);
      return;
    }

    if (reset) setLoading(true);
    else {
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }

    try {
      const offset = reset ? 0 : postsRef.current.length;

      if (mode === "saved") {
        let savedRows = savedPostsRef.current;
        if (reset) {
          const [nextRows, preferences] = await Promise.all([fetchSavedRows(user.id), fetchFeedPreferences(user.id)]);
          preferencesRef.current = preferences;
          savedRows = filterKinfeedPostsForViewer(nextRows, preferences.hiddenPostIds, preferences.mutedMemberIds);
          savedPostsRef.current = savedRows;
          setAllSavedPosts(savedRows);
        }

        const pagePosts = savedRows.slice(offset, offset + KINFEED_PAGE_SIZE);
        const engagement = await fetchSavedEngagement(pagePosts.map((post) => post.id));
        setPosts((current) => (reset ? pagePosts : mergeById(current, pagePosts)));
        setLikes((current) => (reset ? engagement.likes : mergeById(current, engagement.likes)));
        setPostReactions((current) => (reset ? engagement.reactions : mergeById(current, engagement.reactions)));
        setRecentComments((current) => (reset ? engagement.recentComments : mergeById(current, engagement.recentComments)));
        setHasMore(offset + pagePosts.length < savedRows.length);
      } else {
        const page = await fetchKinfeedPage({ userId: user.id, mode, offset, pageSize: KINFEED_PAGE_SIZE });
        setPosts((current) => (reset ? page.posts : mergeById(current, page.posts)));
        setLikes((current) => (reset ? page.likes : mergeById(current, page.likes)));
        setPostReactions((current) => (reset ? page.reactions : mergeById(current, page.reactions)));
        setRecentComments((current) => (reset ? page.recentComments : mergeById(current, page.recentComments)));
        setHasMore(page.hasMore);
      }
    } catch (error) {
      console.error("Could not load Kinfeed:", error);
      toast.error(reset ? "Couldn't load Kinfeed" : "Couldn't load more posts");
    } finally {
      setLoading(false);
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    setPosts([]);
    setLikes([]);
    setPostReactions([]);
    setRecentComments([]);
    setNewPostCount(0);
    restoreAttemptedRef.current = false;
    void loadPage(feedMode, true);

    void Promise.all([fetchSavedFeedPostIds(user.id), fetchFeedPreferences(user.id)])
      .then(([ids, preferences]) => {
        setSavedPostIds(new Set(ids));
        preferencesRef.current = preferences;
      })
      .catch((error) => console.error("Could not load Kinfeed preferences:", error));
  }, [feedMode, loadPage, user?.id]);

  useEffect(() => {
    let active = true;
    const photoValues = posts.flatMap((post) => post.photo_urls?.filter(Boolean) ?? []);
    const videoValues = posts.flatMap((post) => post.video_urls?.filter(Boolean) ?? []);

    void Promise.all([
      resolveSignedStorageUrls("community-photos", photoValues),
      resolveSignedStorageUrls("community-media", videoValues),
    ]).then(([photoMap, videoMap]) => {
      if (!active) return;
      setResolvedPhotoUrls(photoMap);
      setResolvedVideoUrls(videoMap);
    });

    return () => {
      active = false;
    };
  }, [posts]);

  useEffect(() => {
    restoreTargetRef.current = storageKey ? localStorage.getItem(storageKey) : null;
    restoreAttemptedRef.current = false;
  }, [storageKey]);

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`kinfeed-web-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "feed_posts" }, (payload: any) => {
        if (payload.eventType === "INSERT") {
          if (feedModeRef.current !== "saved") setNewPostCount((count) => count + 1);
          return;
        }

        if (payload.eventType === "UPDATE") {
          const nextPost = payload.new;
          setPosts((current) => nextPost?.is_deleted
            ? current.filter((post) => post.id !== nextPost.id)
            : current.map((post) => (post.id === nextPost?.id ? { ...post, ...nextPost, profiles: post.profiles } : post)));
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "feed_post_likes" }, (payload: any) => {
        if (payload.eventType === "INSERT") setLikes((current) => mergeById(
          current.filter((like) => !(
            String(like.id).startsWith("optimistic-")
            && like.post_id === payload.new?.post_id
            && like.user_id === payload.new?.user_id
          )),
          [payload.new],
        ));
        if (payload.eventType === "DELETE") setLikes((current) => current.filter((like) => like.id !== payload.old?.id));
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "feed_post_reactions" }, (payload: any) => {
        if (payload.eventType === "INSERT") setPostReactions((current) => mergeById(
          current.filter((reaction) => !(
            String(reaction.id).startsWith("optimistic-")
            && reaction.post_id === payload.new?.post_id
            && reaction.user_id === payload.new?.user_id
          )),
          [payload.new],
        ));
        if (payload.eventType === "DELETE") setPostReactions((current) => current.filter((reaction) => reaction.id !== payload.old?.id));
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "feed_comments" }, (payload: any) => {
        if (payload.eventType === "DELETE" || payload.new?.is_deleted) {
          setRecentComments((current) => current.filter((comment) => comment.id !== (payload.old?.id ?? payload.new?.id)));
          return;
        }
        if (payload.eventType !== "INSERT" || !postsRef.current.some((post) => post.id === payload.new?.post_id)) return;

        void supabase.from("profiles").select("display_name, avatar_url").eq("id", payload.new.user_id).maybeSingle().then(({ data }) => {
          setRecentComments((current) => {
            const withoutOptimisticMatch = current.filter((comment: any) => !(
              String(comment.id).startsWith("optimistic-")
              && comment.post_id === payload.new.post_id
              && comment.user_id === payload.new.user_id
              && comment.content === payload.new.content
            ));
            return mergeById(withoutOptimisticMatch, [{ ...payload.new, profiles: data } as FeedCommentPreview]);
          });
        });
      })
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "saved_feed_posts", filter: `user_id=eq.${user.id}` },
        () => {
          void fetchSavedFeedPostIds(user.id).then((ids) => setSavedPostIds(new Set(ids)));
          if (feedModeRef.current === "saved") setNewPostCount((count) => count + 1);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !loadingMoreRef.current) void loadPage(feedModeRef.current, false);
    }, { rootMargin: "320px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loadPage]);

  useEffect(() => {
    if (!storageKey || !posts.length) return;
    const saveFromViewport = () => {
      const viewportTop = 156;
      const viewportBottom = window.innerHeight - 132;
      const center = viewportTop + (viewportBottom - viewportTop) / 2;
      const visible = posts
        .map((post) => ({ id: post.id, rect: postRefs.current[post.id]?.getBoundingClientRect() }))
        .filter((entry): entry is { id: string; rect: DOMRect } => Boolean(entry.rect))
        .filter(({ rect }) => rect.bottom > viewportTop && rect.top < viewportBottom);
      const closest = visible.sort((left, right) =>
        Math.abs(left.rect.top + left.rect.height / 2 - center) - Math.abs(right.rect.top + right.rect.height / 2 - center))[0];
      if (closest) rememberLastRead(closest.id);
    };
    const schedule = () => {
      if (saveTimeoutRef.current) window.clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = window.setTimeout(() => window.requestAnimationFrame(saveFromViewport), LAST_READ_SETTLE_MS);
    };
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (saveTimeoutRef.current) window.clearTimeout(saveTimeoutRef.current);
    };
  }, [posts, rememberLastRead, storageKey]);

  useEffect(() => {
    if (!posts.length || restoreAttemptedRef.current) return;
    const targetId = restoreTargetRef.current;
    restoreAttemptedRef.current = true;
    if (!targetId || !posts.some((post) => post.id === targetId)) return;
    window.setTimeout(() => postRefs.current[targetId]?.scrollIntoView({ block: "center", behavior: "auto" }), 80);
  }, [posts]);

  const togglePostReaction = async (postId: string, emoji: string) => {
    if (!user?.id) return;
    const previousLikes = likes;
    const previousReactions = postReactions;
    const currentEmoji = likes.some((like) => like.post_id === postId && like.user_id === user.id)
      ? "❤️"
      : postReactions.find((reaction) => reaction.post_id === postId && reaction.user_id === user.id)?.emoji ?? null;
    const nextEmoji = currentEmoji === emoji ? null : emoji;

    setLikes((current) => {
      const withoutMine = current.filter((like) => !(like.post_id === postId && like.user_id === user.id));
      return nextEmoji === "❤️"
        ? mergeById(withoutMine, [{ id: `optimistic-like-${postId}-${user.id}`, post_id: postId, user_id: user.id }])
        : withoutMine;
    });
    setPostReactions((current) => {
      const withoutMine = current.filter((reaction) => !(reaction.post_id === postId && reaction.user_id === user.id));
      return nextEmoji && nextEmoji !== "❤️"
        ? mergeById(withoutMine, [{ id: `optimistic-reaction-${postId}-${user.id}`, post_id: postId, user_id: user.id, emoji: nextEmoji }])
        : withoutMine;
    });

    const result = await toggleKinfeedPostReaction(postId, emoji);
    if (result.error) {
      setLikes(previousLikes);
      setPostReactions(previousReactions);
      toast.error("Couldn't update your reaction");
      return;
    }

    const post = postsRef.current.find((item) => item.id === postId);
    if (result.data === "❤️" && post?.user_id && post.user_id !== user.id) {
      const { data: profile } = await supabase.from("profiles").select("display_name").eq("id", user.id).maybeSingle();
      await dispatchNotification({
        userId: post.user_id,
        type: "post_like",
        body: `${profile?.display_name ?? "Someone"} liked your post`,
        url: `/community/feed/${postId}`,
        entityType: "post",
        entityId: postId,
      });
    }
  };

  const deletePost = async (postId: string, hiddenByAdmin: boolean) => {
    const { error } = await supabase.from("feed_posts" as any).update({ is_deleted: true } as any).eq("id", postId);
    if (error) {
      toast.error("Couldn't remove this post");
      return;
    }
    setPosts((current) => current.filter((post) => post.id !== postId));
    toast.success(hiddenByAdmin ? "Post removed from Kinfeed" : "Post deleted");
  };

  const saveEdit = async (postId: string) => {
    if (!editText.trim()) return;
    if (!isKinfeedPostWithinLimit(editText)) {
      toast.error(`Posts can contain up to ${KINFEED_POST_MAX_LENGTH.toLocaleString("en-GB")} characters`);
      return;
    }
    const existingPost = postsRef.current.find((post) => post.id === postId);
    const parsed = parseKindredShareContent(existingPost?.content);
    const content = composeKindredContent(editText, parsed.payload);
    const { error } = await supabase.from("feed_posts" as any).update({ content, edited_at: new Date().toISOString() } as any).eq("id", postId);
    if (error) {
      toast.error("Couldn't update this post");
      return;
    }
    setPosts((current) => current.map((post) => post.id === postId ? { ...post, content, edited_at: new Date().toISOString() } : post));
    setEditingId(null);
    toast.success("Post updated");
  };

  const handleToggleSavedPost = async (postId: string) => {
    if (!user?.id) return;
    const wasSaved = savedPostIds.has(postId);
    setSavedPostIds((current) => {
      const next = new Set(current);
      if (wasSaved) next.delete(postId);
      else next.add(postId);
      return next;
    });
    const result = await toggleSavedFeedPost(postId, user.id, wasSaved);
    if (result.error) {
      setSavedPostIds((current) => {
        const next = new Set(current);
        if (wasSaved) next.add(postId);
        else next.delete(postId);
        return next;
      });
      toast.error("Couldn't update saved post");
      return;
    }
    if (wasSaved && feedModeRef.current === "saved") setPosts((current) => current.filter((post) => post.id !== postId));
    toast.success(wasSaved ? "Post removed from saved" : "Post saved");
  };

  const hidePost = async (post: any) => {
    if (!user?.id) return;
    const { error } = await hideFeedPost(user.id, post.id);
    if (error) {
      toast.error("Couldn't hide this post");
      return;
    }
    preferencesRef.current.hiddenPostIds.add(post.id);
    setPosts((current) => current.filter((item) => item.id !== post.id));
    toast.success("Post hidden", {
      action: {
        label: "Undo",
        onClick: () => void unhideFeedPost(user.id, post.id).then(() => loadPage(feedModeRef.current, true)),
      },
    });
  };

  const muteMember = async (post: any) => {
    if (!user?.id || post.user_id === user.id) return;
    const { error } = await muteFeedMember(user.id, post.user_id);
    if (error) {
      toast.error("Couldn't mute this member");
      return;
    }
    preferencesRef.current.mutedMemberIds.add(post.user_id);
    setPosts((current) => current.filter((item) => item.user_id !== post.user_id));
    toast.success(`${post.profiles?.display_name ?? "Member"} muted in Kinfeed`, {
      action: {
        label: "Undo",
        onClick: () => void unmuteFeedMember(user.id, post.user_id).then(() => loadPage(feedModeRef.current, true)),
      },
    });
  };

  const shareOutsideKindred = async (post: any) => {
    const url = buildKindredWebUrl(`/community/feed/${post.id}`);
    const text = parseKindredShareContent(post.content).payload
      ? getKindredSharePreview(post.content)
      : `${post.profiles?.display_name ?? "Someone"} on Kindred: ${getPostPreviewText(post.content)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Kinfeed post", text, url });
      } catch {
        // The member closed the native share sheet.
      }
      return;
    }
    await navigator.clipboard.writeText(`${text}\n${url}`);
    toast.success("Post link copied");
  };

  const sendInlineComment = async (post: any) => {
    const content = inlineComment.trim();
    if (!content || !user?.id || sendingComment) return;
    const optimisticId = `optimistic-${crypto.randomUUID()}`;
    const optimisticComment: FeedCommentPreview = {
      id: optimisticId,
      post_id: post.id,
      content,
      created_at: new Date().toISOString(),
      user_id: user.id,
      parent_id: null,
      profiles: {
        display_name: user.user_metadata?.display_name ?? user.email?.split("@")[0] ?? "You",
        avatar_url: user.user_metadata?.avatar_url ?? null,
      },
    };

    setSendingComment(true);
    setRecentComments((current) => [optimisticComment, ...current]);
    setPosts((current) => current.map((item) => item.id === post.id ? { ...item, comment_count: (item.comment_count ?? 0) + 1 } : item));
    const { error } = await createKinfeedComment(post.id, user.id, content);
    if (error) {
      setRecentComments((current) => current.filter((comment) => comment.id !== optimisticId));
      setPosts((current) => current.map((item) => item.id === post.id ? { ...item, comment_count: Math.max(0, (item.comment_count ?? 1) - 1) } : item));
      setSendingComment(false);
      toast.error("Couldn't add your comment");
      return;
    }

    const actorName = optimisticComment.profiles?.display_name ?? "Someone";
    if (post.user_id !== user.id) {
      await dispatchNotification({
        userId: post.user_id,
        type: "post_reply",
        body: `${actorName} commented on your post`,
        url: `/community/feed/${post.id}`,
        entityType: "post",
        entityId: post.id,
      });
    }
    await dispatchMentionNotifications({
      actorId: user.id,
      actorDisplayName: actorName,
      content,
      mentions: inlineMentions,
      bodyContext: "in a Kinfeed comment",
      entityType: "post",
      entityId: post.id,
      url: `/community/feed/${post.id}`,
    });
    setInlineComment("");
    setInlineMentions([]);
    setSendingComment(false);
  };

  const commentsByPost = useMemo(() => {
    const grouped = new Map<string, FeedCommentPreview[]>();
    for (const comment of recentComments) {
      if (!comment.post_id) continue;
      const next = grouped.get(comment.post_id) ?? [];
      next.push(comment);
      grouped.set(comment.post_id, next);
    }
    for (const comments of grouped.values()) {
      comments.sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime());
    }
    return grouped;
  }, [recentComments]);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const diffHours = (Date.now() - date.getTime()) / 3600000;
    if (diffHours < 1) return `${Math.max(1, Math.floor(diffHours * 60))}m`;
    if (diffHours < 24) return `${Math.floor(diffHours)}h`;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  if (loading) return <AppLoadingState title="Loading Kinfeed" body="Pulling in the latest conversation." />;

  const filteredPosts = filterItemsByKinfeedTopic(posts, selectedTopic);
  const activeTopic = getKinfeedTopicTag(selectedTopic === KINFEED_TOPIC_FILTER_ALL ? null : selectedTopic);

  return (
    <div className="space-y-2.5">
      <IosInstallPrompt />

      <div className="sticky top-4 z-20 -mx-1 rounded-[20px] bg-background/95 px-1 pb-2 pt-1 backdrop-blur-sm">
        <button type="button" onClick={() => navigate("/community/feed/new")} className="w-full text-left">
          <AppCard>
            <AppCardBody py={8}>
              <div className="flex items-center gap-3">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarFallback className="bg-primary/20 text-xs font-semibold text-primary">
                    {(user?.user_metadata?.display_name ?? "Y")[0]}
                  </AvatarFallback>
                </Avatar>
                <AppText className="text-sm text-muted-foreground">What's on your mind?</AppText>
                <Pencil size={16} className="ml-auto text-primary" />
              </div>
            </AppCardBody>
          </AppCard>
        </button>
      </div>

      <PushNotificationPrompt />

      {newPostCount > 0 ? (
        <div className="sticky top-[88px] z-10 flex justify-center">
          <AppButton
            type="button"
            size="sm"
            tone="secondary"
            onPress={() => {
              setNewPostCount(0);
              void loadPage(feedModeRef.current, true).then(() => window.scrollTo({ top: 0, behavior: "smooth" }));
            }}
          >
            {feedMode === "saved" ? "Refresh saved posts" : `View ${newPostCount} new post${newPostCount === 1 ? "" : "s"}`}
          </AppButton>
        </div>
      ) : null}

      <div className="flex flex-col gap-2 rounded-[18px] border border-border/70 bg-background p-2 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <AppSegmentedControl
            value={feedMode}
            options={[
              { value: "local", label: "Local" },
              { value: "latest", label: "Latest" },
              { value: "saved", label: "Saved" },
            ]}
            onChange={(value) => setFeedMode(value as KinfeedFeedMode)}
          />
        </div>
        <AppButton type="button" tone="outline" size="xs" className="shrink-0" onPress={() => setTopicPickerOpen(true)}>
          <span>{activeTopic?.label ?? "All topics"}</span>
          <ChevronDown className="h-3.5 w-3.5" />
        </AppButton>
      </div>

      <AppBottomSheetSurface
        open={topicPickerOpen}
        onOpenChange={setTopicPickerOpen}
        title="Browse by topic"
        description="Choose the conversations you want to see."
      >
        <div className="space-y-2">
          <AppButton type="button" tone={selectedTopic === KINFEED_TOPIC_FILTER_ALL ? "secondary" : "outline"} width="100%" onPress={() => {
            setSelectedTopic(KINFEED_TOPIC_FILTER_ALL);
            setTopicPickerOpen(false);
          }}>
            All posts
          </AppButton>
          {KINFEED_TOPIC_TAGS.map((tag) => (
            <AppButton key={tag.id} type="button" tone={selectedTopic === tag.id ? "secondary" : "outline"} width="100%" onPress={() => {
              setSelectedTopic(tag.id);
              setTopicPickerOpen(false);
            }}>
              {tag.label}
            </AppButton>
          ))}
        </div>
      </AppBottomSheetSurface>

      {filteredPosts.length ? filteredPosts.map((post) => {
        const reactions = groupFeedPostReactions(post.id, likes, postReactions, user?.id);
        const isSaved = savedPostIds.has(post.id);
        const canEditPost = canEditOwnedContentOnly({ viewerId: user?.id, ownerId: post.user_id });
        const canRemovePost = canRemoveKinfeedOrChannelContent({ viewerId: user?.id, ownerId: post.user_id, role });
        const isAdminModeratingPost = isAdminModeratingOthersContent({ viewerId: user?.id, ownerId: post.user_id, role });
        const photos = (post.photo_urls ?? [])
          .map((url: string) => resolveStoredObjectUrl("community-photos", url, resolvedPhotoUrls))
          .filter(Boolean) as string[];
        const videos = (post.video_urls ?? [])
          .map((url: string) => resolveStoredObjectUrl("community-media", url, resolvedVideoUrls))
          .filter(Boolean) as string[];
        const authorName = post.profiles?.display_name ?? "Member";
        const commentPreviews = (commentsByPost.get(post.id) ?? []).slice(0, 2);
        const topicTags = normalizeKinfeedTopicTags(post.topic_tags);
        const commentCount = post.comment_count ?? 0;
        const sharePayload: KindredSharePayload = {
          version: 1,
          type: "feed_post",
          targetId: post.id,
          title: getPostPreviewText(post.content) || `${authorName}'s Kinfeed post`,
          subtitle: `${authorName} on Kinfeed`,
          imageUrl: photos[0] ?? null,
          route: `/community/feed/${post.id}`,
          externalUrl: buildKindredWebUrl(`/community/feed/${post.id}`),
          accessScope: "members",
        };

        return (
          <article
            key={post.id}
            ref={(node) => { postRefs.current[post.id] = node; }}
            data-post-id={post.id}
            className="kindred-feed-card rounded-[22px] px-4 py-4"
          >
            <div className="flex items-start gap-3">
              <button type="button" aria-label={`Open ${authorName}'s profile`} onClick={() => navigate(`/community/member/${post.user_id}`)}>
                <Avatar className="h-11 w-11 shrink-0">
                  {post.profiles?.avatar_url ? <AvatarImage src={post.profiles.avatar_url} /> : null}
                  <AvatarFallback className="bg-primary/20 text-sm font-semibold text-primary">{authorName[0]}</AvatarFallback>
                </Avatar>
              </button>
              <div className="min-w-0 flex-1">
                <button type="button" onClick={() => navigate(`/community/member/${post.user_id}`)} className="block max-w-full text-left">
                  <span className="block truncate text-sm font-semibold text-foreground hover:text-primary">{authorName}</span>
                </button>
                <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                  <span>{formatTime(post.created_at)}</span>
                  {post.profiles?.postcode ? <span>{formatPublicLocation(post.profiles.postcode, post.profiles.country_code)}</span> : null}
                </div>
              </div>
              <MessageActions
                menuTitle="Post actions"
                menuDescription="Choose what to do with this Kinfeed post."
                triggerLabel="More post actions"
                actions={[
                  { id: "save", label: isSaved ? "Remove from saved" : "Save post", icon: <Bookmark size={16} />, onSelect: () => handleToggleSavedPost(post.id) },
                  { id: "hide", label: "Hide from my feed", icon: <EyeOff size={16} />, onSelect: () => hidePost(post) },
                  ...(post.user_id !== user?.id ? [
                    { id: "mute", label: `Mute ${authorName}`, icon: <VolumeX size={16} />, onSelect: () => muteMember(post) },
                    { id: "report", label: "Report post", icon: <Flag size={16} />, onSelect: () => setReportPostId(post.id) },
                  ] : []),
                  { id: "external-share", label: "Share outside Kindred", icon: <Share2 size={16} />, onSelect: () => shareOutsideKindred(post) },
                ]}
                onEdit={canEditPost ? () => {
                  const parsed = parseKindredShareContent(post.content);
                  setEditingId(post.id);
                  setEditText(parsed.payload ? parsed.text : post.content);
                } : undefined}
                onDelete={canRemovePost ? () => void deletePost(post.id, isAdminModeratingPost) : undefined}
                deleteLabel={isAdminModeratingPost ? "Remove from Kinfeed" : "Delete post"}
                showEdit={canEditPost}
              />
            </div>

            <div className="mt-3">
              {editingId === post.id ? (
                <div className="space-y-2">
                  <AppTextArea value={editText} onChangeText={setEditText} maxLength={KINFEED_POST_MAX_LENGTH} rows={3} />
                  <AppButtonRow>
                    <AppButton type="button" size="sm" onPress={() => void saveEdit(post.id)}>Save</AppButton>
                    <AppButton type="button" tone="outline" size="sm" onPress={() => setEditingId(null)}>Cancel</AppButton>
                  </AppButtonRow>
                </div>
              ) : (
                <>
                  {topicTags.length ? (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {topicTags.map((tagId) => (
                        <span key={tagId} className="rounded-full border border-primary/10 bg-primary/5 px-2.5 py-1 text-[11px] font-semibold text-primary">
                          {getKinfeedTopicTag(tagId)?.label ?? tagId}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <KindredSharedContent collapsible content={post.content} linkPreview={post.link_preview} textClassName="text-sm leading-7 text-foreground" />
                  {post.edited_at ? <span className="mt-1 inline-block text-[10px] text-muted-foreground">Edited</span> : null}
                </>
              )}
            </div>

            <KinfeedMedia
              photos={photos}
              videos={videos}
              className="mt-3"
              onOpenPhoto={(index) => setFullScreenImages({ images: photos, index })}
            />

            {(Object.keys(reactions).length > 0 || commentCount > 0) ? (
              <div className="mt-3 flex min-h-8 items-center justify-between gap-3 border-b border-border/60 pb-2">
                <ReactionBar reactions={reactions} onToggleReaction={(emoji) => togglePostReaction(post.id, emoji)} variant="summary" disabled />
                {commentCount > 0 ? (
                  <button type="button" onClick={() => navigate(`/community/feed/${post.id}`)} className="shrink-0 text-xs text-muted-foreground hover:text-primary">
                    {formatCommentCount(commentCount)}
                  </button>
                ) : null}
              </div>
            ) : null}

            {commentPreviews.length ? (
              <div className="mt-3 space-y-2">
                {commentPreviews.map((comment) => (
                  <div key={comment.id} className="flex items-start gap-2.5 rounded-[14px] bg-muted/60 px-3 py-2.5">
                    <button type="button" onClick={() => comment.user_id && navigate(`/community/member/${comment.user_id}`)}>
                      <Avatar className="h-7 w-7 shrink-0">
                        {comment.profiles?.avatar_url ? <AvatarImage src={comment.profiles.avatar_url} /> : null}
                        <AvatarFallback className="bg-primary/20 text-[11px] font-semibold text-primary">{(comment.profiles?.display_name ?? "M")[0]}</AvatarFallback>
                      </Avatar>
                    </button>
                    <button type="button" onClick={() => navigate(`/community/feed/${post.id}`)} className="min-w-0 flex-1 text-left">
                      <span className="block text-xs font-semibold text-foreground">{comment.profiles?.display_name ?? "Member"}</span>
                      <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{getFeedReplyPreviewText(comment.content)}</span>
                    </button>
                  </div>
                ))}
                {commentCount > commentPreviews.length ? (
                  <button type="button" onClick={() => navigate(`/community/feed/${post.id}`)} className="text-xs font-semibold text-muted-foreground hover:text-primary">
                    View all {formatCommentCount(commentCount)}
                  </button>
                ) : null}
              </div>
            ) : null}

            <div className="mt-3 grid grid-cols-3 items-stretch border-t border-border/60 pt-1">
              <ReactionBar
                reactions={reactions}
                onToggleReaction={(emoji) => togglePostReaction(post.id, emoji)}
                onPrimaryAction={() => togglePostReaction(post.id, "❤️")}
                variant="action"
              />
              <button
                type="button"
                onClick={() => {
                  setExpandedCommentPostId((current) => current === post.id ? null : post.id);
                  setInlineComment("");
                  setInlineMentions([]);
                }}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md px-2 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted/70 hover:text-primary"
              >
                <MessageCircle size={17} />
                <span>Comment</span>
              </button>
              <div className="flex min-w-0 items-stretch justify-center">
                <KindredShareSheet
                  payload={sharePayload}
                  triggerLabel="Share post"
                  triggerTone="ghost"
                  triggerContent={<><Share2 size={17} /><span>Share</span></>}
                />
              </div>
            </div>

            {expandedCommentPostId === post.id ? (
              <div className="mt-2 flex items-end gap-2 rounded-[16px] bg-muted/45 p-2.5">
                <div className="min-w-0 flex-1">
                  <MentionComposerField
                    value={inlineComment}
                    onChangeText={setInlineComment}
                    mentions={inlineMentions}
                    onMentionsChange={setInlineMentions}
                    lookupCandidates={(query) => searchAllMentionSuggestions(query, user?.id)}
                    placeholder="Write a comment..."
                    inputProps={{
                      onKeyDown: (event: any) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                          event.preventDefault();
                          void sendInlineComment(post);
                        }
                      },
                    }}
                  />
                </div>
                <AppButton type="button" size="sm" onPress={() => void sendInlineComment(post)} disabled={!inlineComment.trim() || sendingComment}>
                  <Send size={15} />
                  <span className="sr-only">Send comment</span>
                </AppButton>
              </div>
            ) : null}
          </article>
        );
      }) : (
        <AppCard>
          <AppCardBody>
            <AppEmptyState
              title={feedMode === "saved" ? "No saved posts" : activeTopic ? `No ${activeTopic.label.toLowerCase()} posts yet` : "No posts in Kinfeed yet"}
              body={feedMode === "saved" ? "Posts you save will appear here." : "Start a conversation when you have something to share."}
            />
          </AppCardBody>
        </AppCard>
      )}

      <div ref={loadMoreRef} className="flex min-h-14 items-center justify-center py-2" aria-live="polite">
        {loadingMore ? <AppText color="$colorMuted" fontSize="$sm">Loading more posts...</AppText> : null}
      </div>

      {fullScreenImages ? (
        <FullScreenImage
          images={fullScreenImages.images}
          currentIndex={fullScreenImages.index}
          onClose={() => setFullScreenImages(null)}
          onNavigate={(index) => setFullScreenImages({ ...fullScreenImages, index })}
        />
      ) : null}

      <ReportSheet
        open={Boolean(reportPostId)}
        onOpenChange={(open) => { if (!open) setReportPostId(null); }}
        contentType="feed_post"
        contentId={reportPostId ?? undefined}
      />
    </div>
  );
};

export default CommunityFeed;
